import React, { useEffect, useState } from 'react';
import { getLicenses, searchPackages, uploadPackage, deletePackage, downloadPackage } from '../../services/api';
import Button from '../ui/Button';
import Pager from '../ui/Pager';
import SortableTh from '../ui/SortableTh';
import { pill } from '../pill';
import Highlight from '../ui/Highlight';

const ENVIRONMENTS = ['INTEGRATION', 'VALIDATION', 'PREPROD', 'PROD', 'TEST'];
const EMPTY_FILTERS = { client: '', environment: '', state: 'ACTIVE', q: '' };

/**
 * Packages : liste paginée, triée et filtrée côté API (GET /api/packages/search) —
 * plusieurs centaines de packages par client, on ne charge plus tout d'un coup.
 */
export default function PackagesPage() {
  const [result, setResult] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [licenses, setLicenses] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [query, setQuery] = useState('');            // saisie libre, appliquée après 300 ms
  const [sort, setSort] = useState({ key: 'uploadedAt', dir: 'desc' });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ version: '', releaseNotes: '', licenseId: '', environment: '', idempotent: true });
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    searchPackages({
      client: filters.client || undefined,
      environment: filters.environment || undefined,
      state: filters.state,
      q: filters.q || undefined,
      page, size, sort: sort.key, dir: sort.dir,
    }).then(setResult).finally(() => setLoading(false));
  };
  useEffect(() => { getLicenses().then(setLicenses); }, []);
  useEffect(load, [filters, sort, page, size]);

  // Recherche libre : pas une requête par frappe
  useEffect(() => {
    const t = setTimeout(() => {
      if (query !== filters.q) { setFilters(f => ({ ...f, q: query })); setPage(0); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const setFilter = field => e => { setFilters({ ...filters, [field]: e.target.value }); setPage(0); };
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setQuery(''); setPage(0); };
  const onSort = s => { setSort(s); setPage(0); };
  const packages = result.content;

  const handleUpload = async e => {
    e.preventDefault();
    if (!file) { setError('Please select a file'); return; }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('version', form.version);
      if (form.releaseNotes) fd.append('releaseNotes', form.releaseNotes);
      if (form.licenseId) fd.append('licenseId', form.licenseId);
      if (form.environment) fd.append('environment', form.environment);
      fd.append('idempotent', String(form.idempotent));
      await uploadPackage(fd);
      setShowForm(false);
      setForm({ version: '', releaseNotes: '', licenseId: '', environment: '', idempotent: true });
      setFile(null);
      load();
    } catch (err) {
      setError('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this package?')) return;
    await deletePackage(id);
    load();
  };

  const handleDownload = async (id, fileName) => {
    const blob = await downloadPackage(id);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatSize = bytes => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>Update Packages</h1>
        <Button variant={showForm ? 'secondary' : 'primary'} onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Cancel' : '+ Upload Package'}
        </Button>
      </div>

      {/* Upload form */}
      {showForm && (
        <form onSubmit={handleUpload} style={{ ...card, marginBottom: '1.5rem' }}>
          <div style={{ fontWeight: 600, color: '#6b7280', marginBottom: '1rem' }}>New Package</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

            {/* File drop zone */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>File</label>
              <div
                style={{ ...dropZone, ...(file ? { borderColor: '#1a7f37' } : {}) }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}>
                {file
                  ? <span style={{ color: '#1a7f37' }}>✓ {file.name} ({formatSize(file.size)})</span>
                  : <span style={{ color: '#8a8f99' }}>Drag & drop or <label style={{ color: '#005cc5', cursor: 'pointer' }}>
                      browse<input type="file" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
                    </label></span>}
              </div>
            </div>

            <div>
              <label style={labelStyle}>Version (semver)</label>
              <input style={inputStyle} placeholder="1.2.3" value={form.version}
                     onChange={e => setForm({ ...form, version: e.target.value })} required
                     pattern="\d+\.\d+\.\d+" title="Format: 1.2.3" />
            </div>

            <div>
              <label style={labelStyle}>Client (vide = global, tous les clients)</label>
              <select style={inputStyle} value={form.licenseId}
                      onChange={e => setForm({ ...form, licenseId: e.target.value })}>
                <option value="">🌍 Global (all clients)</option>
                {licenses.map(l => (
                  <option key={l.id} value={l.id}>{l.clientName} — {l.licenseKey}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Environnement (canal)</label>
              <select style={inputStyle} value={form.environment}
                      onChange={e => setForm({ ...form, environment: e.target.value })}>
                <option value="">Tous les environnements</option>
                {['INTEGRATION', 'VALIDATION', 'PREPROD', 'PROD', 'TEST'].map(env => (
                  <option key={env} value={env}>{env}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>
                <input type="checkbox" checked={form.idempotent}
                       onChange={e => setForm({ ...form, idempotent: e.target.checked })}
                       style={{ marginRight: '0.5rem' }} />
                Idempotent (ré-applicable sans risque)
              </label>
              <div style={{ fontSize: '0.75rem', color: '#8a8f99', marginTop: '0.25rem' }}>
                Décoché : jamais redistribué automatiquement à une instance qui l'a déjà tenté.
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Release Notes</label>
              <textarea style={{ ...inputStyle, height: '80px', resize: 'vertical' }}
                        placeholder="Describe what's new in this version..."
                        value={form.releaseNotes}
                        onChange={e => setForm({ ...form, releaseNotes: e.target.value })} />
            </div>
          </div>

          {error && <p style={{ color: '#c62828', fontSize: '0.875rem', marginTop: '0.5rem' }}>{error}</p>}

          <div style={{ textAlign: 'right', marginTop: '1rem' }}>
            <Button type="submit" variant="primary" disabled={uploading}>
              {uploading ? 'Uploading...' : '⬆ Upload'}
            </Button>
          </div>
        </form>
      )}

      {/* Filtres */}
      <div style={{ ...card, marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '2 1 220px' }}>
          <label style={labelStyle}>Recherche (nom, version, fichier, notes)</label>
          <input style={inputStyle} value={query} onChange={e => setQuery(e.target.value)} placeholder="SNCFIMMO-422, 1.1.0…" />
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={labelStyle}>Client</label>
          <select style={inputStyle} value={filters.client} onChange={setFilter('client')}>
            <option value="">Tous</option>
            <option value="global">🌍 Global</option>
            {licenses.map(l => <option key={l.id} value={l.id}>{l.clientName}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={labelStyle}>Environnement</label>
          <select style={inputStyle} value={filters.environment} onChange={setFilter('environment')}>
            <option value="">Tous</option>
            <option value="none">Sans ciblage (tous les env.)</option>
            {ENVIRONMENTS.map(env => <option key={env} value={env}>{env}</option>)}
          </select>
        </div>
        <div style={{ flex: '1 1 140px' }}>
          <label style={labelStyle}>État</label>
          <select style={inputStyle} value={filters.state} onChange={setFilter('state')}>
            <option value="ACTIVE">Actifs</option>
            <option value="DELETED">Supprimés</option>
            <option value="ALL">Tous</option>
          </select>
        </div>
        <Button onClick={resetFilters}>Réinitialiser</Button>
      </div>

      {/* Packages list */}
      <div style={{ ...card, overflowX: 'auto', opacity: loading ? 0.6 : 1 }}>
        <table style={{ width: '100%', minWidth: '1240px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#8a8f99', fontSize: '0.8rem', textTransform: 'uppercase', borderBottom: '1px solid #cdcdcd' }}>
              <SortableTh label="Name" sortKey="name" sort={sort} onSort={onSort} />
              <SortableTh label="Version" sortKey="version" sort={sort} onSort={onSort} />
              <SortableTh label="Order" sortKey="applyOrder" sort={sort} onSort={onSort} />
              <SortableTh label="File" />
              <SortableTh label="Size" sortKey="fileSize" sort={sort} onSort={onSort} />
              <SortableTh label="Client" sortKey="client" sort={sort} onSort={onSort} />
              <SortableTh label="Environnement" sortKey="environment" sort={sort} onSort={onSort} />
              <SortableTh label="Téléchargé" sortKey="downloads" sort={sort} onSort={onSort} />
              <SortableTh label="Release Notes" />
              <SortableTh label="Uploaded" sortKey="uploadedAt" sort={sort} onSort={onSort} />
              <SortableTh label="État" />
              <SortableTh label="" />
            </tr>
          </thead>
          <tbody>
            {packages.map(pkg => (
              <tr key={pkg.id} style={{ borderBottom: '1px solid #ececec' }}
                  className="ui-row">
                <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>{pkg.name ? <Highlight text={pkg.name} query={filters.q} /> : '—'}</td>
                <td style={td}>
                  <span style={{ background: '#232853', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.82rem' }}>
                    v<Highlight text={pkg.version} query={filters.q} />
                  </span>
                </td>
                <td style={{ ...td, color: '#6b7280', fontSize: '0.85rem' }}>{pkg.applyOrder ?? '—'}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.82rem', color: '#6b7280', whiteSpace: 'nowrap' }}><Highlight text={pkg.fileName} query={filters.q} /></td>
                <td style={{ ...td, color: '#6b7280', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{formatSize(pkg.fileSize)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {pkg.clientName
                    ? <span style={{ color: '#5f37a8' }}>👤 {pkg.clientName}</span>
                    : <span style={{ color: '#1a7f37' }}>🌍 Global</span>}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {pkg.environment
                    ? <span style={{ background: '#cdcdcd', color: '#555555', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>{pkg.environment}</span>
                    : <span style={{ color: '#8a8f99', fontSize: '0.82rem' }}>tous</span>}
                  {pkg.idempotent === false &&
                    <span title="Non idempotent : jamais redistribué automatiquement après une tentative"
                          style={{ marginLeft: '0.4rem', background: '#fdeee0', color: '#b45309', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem' }}>⚠ non idem.</span>}
                </td>
                <td style={{ ...td, fontSize: '0.8rem', color: '#6b7280' }}
                    title={pkg.lastDownloadedAt ? 'Dernier : ' + new Date(pkg.lastDownloadedAt).toLocaleString('fr-FR') : ''}>
                  {pkg.downloadCount > 0
                    ? <>{pkg.downloadCount}×<br /><span style={{ fontSize: '0.72rem' }}>{new Date(pkg.lastDownloadedAt).toLocaleString('fr-FR')}</span></>
                    : '—'}
                </td>
                <td style={{ ...td, maxWidth: '250px' }}>
                  <span style={{ color: '#6b7280', fontSize: '0.82rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={pkg.releaseNotes}>
                    {/* Notes tronquées : extrait centré sur le terme trouvé (sinon invisible) */}
                    {pkg.releaseNotes ? <Highlight text={pkg.releaseNotes} query={filters.q} radius={12} /> : '—'}
                  </span>
                </td>
                <td style={{ ...td, fontSize: '0.82rem', color: '#8a8f99', whiteSpace: 'nowrap' }}>
                  {new Date(pkg.uploadedAt).toLocaleDateString('fr-FR')}<br/>
                  <span style={{ fontSize: '0.75rem' }}>{pkg.uploadedBy}</span>
                </td>
                <td style={td}>
                  {pkg.active
                    ? <span style={pill('#e2f3e8', '#1a7f37')}>Actif</span>
                    : <span style={pill('#fdecea', '#b3261e')}>Supprimé</span>}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {/* Supprimé = plus téléchargeable (le back renvoie 404) : pas d'action */}
                  {pkg.active && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button size="sm" onClick={() => handleDownload(pkg.id, pkg.fileName)}>⬇ Download</Button>
                      <Button size="sm" variant="danger" onClick={() => handleDelete(pkg.id)} title="Supprimer">✕</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!loading && packages.length === 0 && (
              <tr><td colSpan={12} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '3rem' }}>
                Aucun package pour ces filtres
              </td></tr>
            )}
          </tbody>
        </table>
        <Pager page={page} totalPages={result.totalPages} totalElements={result.totalElements}
               size={size} onPage={setPage} onSize={s => { setSize(s); setPage(0); }} />
      </div>
    </div>
  );
}

const card = { background: '#ffffff', borderRadius: '12px', padding: '1.5rem' };
const td = { padding: '0.85rem 1rem', color: '#444444' };
const labelStyle = { display: 'block', color: '#6b7280', fontSize: '0.8rem', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '0.6rem', background: '#f5f5f5', border: '1px solid #cdcdcd', color: '#444444', borderRadius: '6px', boxSizing: 'border-box', fontSize: '0.9rem' };
const dropZone = { border: '2px dashed #cdcdcd', borderRadius: '8px', padding: '1.5rem', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s' };
